/** @jsx jsx */

import * as React from "react";
import { css, jsx } from "@emotion/core";
import { DESIGN_TOKEN } from "Config/design_token";
import { SVGWrapper } from "Views/presentational/svg_wrapper";

/** Props needed to render a single Nav item. */
export type NavItemProps = {
  /** The title of the navigation item as displayed by the user. */
  title: string;
  /** The item's link destination. */
  destination: string;
  /** If this item's destination is currently active. */
  isActive: boolean;
  /** Handles click events to the link. */
  onClick: (destination: NavItemProps["destination"]) => void;
  /** Determines how the navigation item should render. */
  layout: "COMPACT" | "FULL";
};

function NavItem(props: NavItemProps): JSX.Element {
  const { title, destination, isActive, onClick, layout } = props;

  return (
    <a
      css={css`
        display: flex;
        position: relative;
        width: 100%;
        padding: 1.5rem 3rem 1.5rem 3rem;
        background-color: ${DESIGN_TOKEN.colors.swatch_3};
        border-left: 0.5rem solid ${DESIGN_TOKEN.colors.swatch_3};
        font-family: ${DESIGN_TOKEN.fonts.font_2.family};
        font-size: 1.4rem;
        text-decoration: none;
        color: ${DESIGN_TOKEN.colors.swatch_1};
        transition: padding-left 0.15s ease;

        &:hover,
        &:focus {
          cursor: pointer;
          border-left-color: ${DESIGN_TOKEN.colors.swatch_1};
          padding-left: 5rem;
        }

        ${isActive &&
          `
          cursor: pointer;
          border-left-color: ${DESIGN_TOKEN.colors.swatch_1};
          padding-left: 5rem;
        `}

        ${layout === "FULL" &&
          `
          padding: 1.2rem 3rem 1.2rem 2rem;
          font-size: 1rem;
          &:hover,
          &:focus {
            padding-left: 3rem;
          }
        `}
        ${(layout === 'FULL' && isActive) &&
          `
          padding-left: 3rem;
        `}
      `}
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
        onClick(destination);
      }}
      onKeyUp={(event: React.KeyboardEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (event.key === "enter") {
          onClick(destination);
        }
      }}
      href={`/${destination}`}
    >
      {title}
      {isActive && (
        <span
          css={css`
            display: inline-flex;
            position: absolute;
            top: 0rem;
            right: 1rem;
            width: auto;
            height: 100%;
            align-items: center;
            justify-content: center;
          `}
          aria-hidden="true"
        >
          {layout === "FULL" && (
            <SVGWrapper
              css={css`
                width: 1rem;
                height: 1rem;
              `}
              viewBox="0 0 50 100"
              preserveAspectRatio="xMidYMid meet"
            >
              <path
                d="
                  M0  0
                  L50 50
                  L0  100
                "
                stroke={`${DESIGN_TOKEN.colors.swatch_1}`}
                fill="none"
                strokeWidth="0.5rem"
              />
            </SVGWrapper>
          )}
        </span>
      )}
    </a>
  );
}

const navItemMemo = React.memo<NavItemProps>(NavItem);
export { navItemMemo as NavItem };
